import { StarIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";
import { useModalStore } from "@/stores/use-modal-store";
import { useSegment } from "@/hooks/use-segment";
import { useCreateImages } from "@/api/studio/exports/queries";
import { EditStudyModal } from "@/features/reports/modals/edit-study-modal";
import { ExportVisualsModal } from "@/features/reports/modals/export-visuals-modal";
import { PencilSquareIcon, Square2StackIcon } from "@heroicons/react/20/solid";
import { ArrowUpOnSquareIcon } from "@heroicons/react/24/outline";

/* Prop Types */
interface VideoCopilotSummaryProps {
  projectId: string;
  studyId: string;
  summary: string;
}

/**
 * VideoCopilotSummary component
 *
 * Renders the Copilot study summary header: icon + label, copy/edit-study/export actions,
 * and the summary sentence itself.
 */
export const VideoCopilotSummary = ({ projectId, studyId, summary }: VideoCopilotSummaryProps) => {
  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Queries */
  const { mutateAsync: createImages } = useCreateImages();

  /* Stores */
  const { open } = useModalStore((state) => state.modalActions);

  /* Handlers */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      toast({ title: "Summary copied to clipboard", type: "success" });
    } catch {
      toast({ title: "Failed to copy summary, please try again", type: "error" });
    }
  };

  const handleEditStudy = () => {
    // Segment tracking
    segmentTrackEvent("Study Editing Initiated", { projectId, studyId, studyType: "video" });

    open(<EditStudyModal projectId={projectId} studyId={studyId} />);
  };

  const handleExport = async () => {
    // Segment tracking
    segmentTrackEvent("Study Exported", { projectId, studyId, exportType: "Visuals", studyType: "video" });

    const { taskId } = await createImages({ projectId, studyId, type: "video" });
    open(<ExportVisualsModal projectId={projectId} studyId={studyId} taskId={taskId} />);
  };

  /* Render */
  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex gap-2.5 items-center w-full">
        <div className="flex flex-1 gap-1.5 items-center">
          <StarIcon className="size-4.5 text-white" />
          <span className="text-base font-semibold">Copilot Summary</span>
        </div>

        <Button variant="ghost" onClick={handleCopy} aria-label="Copy summary">
          <Square2StackIcon className="size-4.5" />
        </Button>
        <Button onClick={handleEditStudy}>
          <PencilSquareIcon className="size-3.5" />
          Edit study
        </Button>
        {/* TODO: Re-enable once export is ready for video studies */}
        <Button onClick={handleExport} disabled>
          <ArrowUpOnSquareIcon className="size-3.5" />
          Export
        </Button>
      </div>

      <h1 className="text-4xl">{summary}</h1>
    </div>
  );
};
